"use client";

import './index.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useId, useEffect, useState, useCallback } from 'react';
import { validateSurface } from '../../globals.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import getBrowser from '../../utilities/check-browser/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import styles from './tooltip.module.scss.js';
import MobileDialog from './mobile-dialog.js';
import TooltipPopover from './popover.js';
import { ButtonIcon } from '../../buttons/button-icon/index.js';
import Info from '../../icons/component-icons/info/index.js';

/**
 * @component Tooltip
 * @package Tooltip
 * @document ../_docs/code/Tooltip.editor.tsx
 * @document ../_docs/code/Tooltip.snippet.tsx
 * @document ../_docs/code/Tooltip.import.tsx
 */
const Tooltip = ({ disabled = false, id, ariaLabel, surface: surfaceProp = 'lightPrimary', fakeMobileDevice, closeButtonText = 'Close', children, title, size = 'medium', containerId, onClick, 'data-testid': testId = 'tooltip', iconColor = undefined, 'data-track': dataTrack, 'data-track-ignore': dataTrackIgnore, 'data-clickstream': dataClickStream, 'data-analyticstrack': dataAnalyticsTrack, renderAnchorElement, maxHeight = 212, defaultPopoverPosition = 'above', zIndex = 1, colorMode = 'light', tabIndex, }) => {
    const tooltipWrapperStyles = [styles.tooltipWrapper];
    const customAnchorWrapperStyles = [styles.customAnchorWrapper];
    const anchorWrapperStyles = [styles.anchorWrapper];
    const anchorButtonWrapperStyles = [styles.anchorButtonWrapper];
    const tooltipId = useRef(undefined);
    const touchStart = useRef(null);
    const touchEnd = useRef(null);
    const _useId = useId();
    const surface = validateSurface(surfaceProp);
    useEffect(() => {
        if (!tooltipId.current) {
            tooltipId.current = id || _useId;
        }
    }, [_useId, id]);
    const scrollableId = `${tooltipId.current}-scroll`;
    const tooltipWrapperId = `${tooltipId.current}-wrapper`;
    const ariaId = `${tooltipId.current}-tooltip`;
    const isAndroidDevice = getOS() === 'android';
    const isMobileDevice = checkIfMobileDevice() || fakeMobileDevice;
    const isFirefox = getBrowser() === 'firefox';
    const isOsx = getOS() === 'osx';
    const [showDialog, setShowDialog] = useState(false); // boolean state that determines if popover is displayed
    const [persistDialog, setPersistDialog] = useState(false); // boolean state that persists dialog if user has clicked on anchor element
    const [keyboardFocused, setKeyboardFocused] = useState(false);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);
    const [unmounting, setUnmounting] = useState(false);
    const [hovered, setHovered] = useState(false);
    const anchorWithRef = useRef(null);
    const tooltipAnchorRef = useRef(null);
    const openTooltipTimeout = useRef();
    const closeTooltipTimeout = useRef();
    const animationTimeout = useRef();
    if (disabled) {
        tooltipWrapperStyles.push(styles.disabled);
        anchorButtonWrapperStyles.push(styles.disabled);
    }
    if (surface === 'darkPrimary') {
        anchorButtonWrapperStyles.push(styles.dark);
    }
    if (showDialog) {
        anchorWrapperStyles.push(styles.showDialog);
        customAnchorWrapperStyles.push(styles.showDialog);
    }
    const _onClickOutside = useCallback((e) => {
        // check if current event contains ref if not close dialog
        const target = e.target;
        if (tooltipAnchorRef.current && (target === null || target === void 0 ? void 0 : target.closest('[data-tooltip]'))) {
            return;
        }
        if (tooltipAnchorRef.current &&
            !tooltipAnchorRef.current.contains(e.target) &&
            target &&
            target.tagName !== 'HTML' &&
            target.getAttribute('data-tooltip') !== ariaId &&
            target.parentNode &&
            target.parentNode.getAttribute('data-tooltip') !==
                ariaId &&
            target.parentNode.id !== ariaId &&
            target.getAttribute('id') !==
                `scrollable-vertical-track-${scrollableId}` &&
            target.parentNode.parentNode.id !==
                `scrollable-content-${scrollableId}` &&
            target.parentNode.parentNode.id !==
                `scrollable-vertical-track-${scrollableId}` &&
            target.id !== `${tooltipId.current}-tooltip`) {
            setKeyboardFocused(false);
            setPersistDialog(false);
            setHovered(false);
            setShowDialog(false);
        }
    }, [ariaId, scrollableId]);
    useEffect(() => {
        /* istanbul ignore else -- @preserve */
        if (typeof window !== 'undefined') {
            document.addEventListener('mousedown', _onClickOutside);
        }
        return () => {
            /* istanbul ignore else -- @preserve */
            if (typeof window !== 'undefined') {
                /* istanbul ignore else -- @preserve */
                if (!isMobileDevice)
                    document.removeEventListener('mousedown', _onClickOutside);
            }
        };
    }, [_onClickOutside, isMobileDevice]);
    useEffect(() => {
        if (keyboardFocused) {
            if (!showDialog) {
                setHasScrollableContent(undefined);
            }
            tooltipAnchorRef.current.focus();
        }
    }, [showDialog, keyboardFocused]);
    const _closeDialog = (e) => {
        if (isMobileDevice) {
            e.stopPropagation();
            setUnmounting(true);
            closeTooltipTimeout.current = setTimeout(() => {
                setShowDialog(false);
                setUnmounting(false);
            }, 100);
        }
        else {
            setShowDialog(false);
        }
    };
    const _openDialog = () => {
        setShowDialog(true);
    };
    /* istanbul ignore next -- @preserve */
    const _dialogOnKeyDown = (e, show) => {
        // Close dialog and refocus anchor on esc space or enter key press
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            if (show !== undefined)
                setShowDialog(show);
            const anchor = tooltipAnchorRef.current;
            anchor.focus();
        }
    };
    const _onMouseEnter = (e) => {
        if (isMobileDevice)
            return;
        /* istanbul ignore else -- @preserve */
        if (e.target.parentNode.id !==
            `scrollable-content-${scrollableId}`) {
            if (keyboardFocused) {
                setKeyboardFocused(false);
            }
            openTooltipTimeout.current = setTimeout(() => {
                setHovered(true);
                setShowDialog(true);
            }, 200);
            /* istanbul ignore else -- @preserve */
            if (anchorWithRef.current !== null &&
                anchorWithRef.current.props.onMouseEnter) {
                anchorWithRef.current.props.onMouseEnter(e);
            }
        }
    };
    const _onMouseLeave = (e) => {
        if (isMobileDevice)
            return;
        /* istanbul ignore next -- @preserve */
        if (persistDialog && showDialog) {
            return;
        }
        // clear time out to prevent showDialog being set to true unintentionally
        clearTimeout(openTooltipTimeout.current);
        setHovered(false);
        setShowDialog(false);
        setKeyboardFocused(false);
        /* istanbul ignore next -- @preserve */
        if (anchorWithRef.current !== null &&
            anchorWithRef.current.props.onMouseLeave) {
            anchorWithRef.current.props.onMouseLeave(e);
        }
    };
    const onTouchStart = (e) => {
        touchEnd.current = null;
        touchStart.current = e.targetTouches[0].clientX;
    };
    const onTouchMove = (e) => {
        touchEnd.current = e.targetTouches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (!touchStart.current || !touchEnd.current)
            return;
        const distance = touchStart.current - touchEnd.current;
        const isRightSwipe = distance < -50;
        if (isRightSwipe) {
            _closeDialog(e);
        }
    };
    const _onClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick)
            onClick(e);
        /* when using JAWS + keyboard interactions are handled by
        the onClick event. If this is the case, it wont be hovered, so show the dialog. */
        const tooltipDialog = typeof document !== 'undefined' &&
            document.getElementById(`${tooltipId.current}-dialog`);
        /* istanbul ignore next -- @preserve */
        if (hovered) {
            if (persistDialog) {
                setPersistDialog(false);
                setShowDialog(false);
            }
            else {
                setPersistDialog(true);
            }
            /**
             * if youre clicking on the tooltip dialog (mobile device dialog), dont do anything.
             * this shouldnt dismiss the tooltip
             */
        }
        else if (tooltipDialog &&
            tooltipDialog.contains(e.target)) ;
        else if (isMobileDevice && showDialog) {
            animationTimeout.current = setTimeout(() => {
                setShowDialog(false);
            }, 100);
            /**
             * treat this as a desktop dialog. when clicking the dialog,
             */
        }
        else {
            setPersistDialog(false);
            setShowDialog(!showDialog);
        }
    };
    const _onFocus = (e) => {
        e.stopPropagation();
        e.preventDefault();
        /*  if the focus came from keyboard set it true,
        if it came from mouse click, it will be set to false in _onClick */
        /* istanbul ignore else -- @preserve */
        if (!hovered && !keyboardFocused) {
            setKeyboardFocused(true);
        }
    };
    const _renderMobileTooltipDialog = () => {
        if (showDialog && !disabled) {
            return (jsx(MobileDialog, { unmounting: unmounting, title: title, closeButtonText: closeButtonText, dialogId: `${tooltipId.current}-dialog`, open: showDialog, surface: surface, colorMode: colorMode, body: children, onTouchStart: onTouchStart, onTouchMove: onTouchMove, onTouchEnd: onTouchEnd, onClick: _closeDialog, isAndroidDevice: isAndroidDevice }));
        }
        return null;
    };
    const _renderTooltipPopover = () => {
        if (showDialog && !disabled) {
            return (jsx(TooltipPopover, { "data-testid": `${testId}-popover`, persist: persistDialog, keepDialogActiveOnMouseEnter: !keyboardFocused ? _openDialog : undefined, customAnchorElement: renderAnchorElement ? tooltipAnchorRef.current : undefined, content: children, closeDialog: _closeDialog, title: title, ariaId: ariaId, defaultPopoverPosition: defaultPopoverPosition, zIndex: zIndex, colorMode: colorMode, tooltipId: tooltipId.current, keyboardFocused: keyboardFocused, toolTipAnchorRef: tooltipAnchorRef.current, showDialog: showDialog, surface: surface, size: size, containerId: containerId, tooltipWrapperId: tooltipWrapperId, onPopoverKeyDown: _dialogOnKeyDown, setHasScrollableContent: setHasScrollableContent, hasScrollableContent: hasScrollableContent, isFirefox: isFirefox, scrollableId: scrollableId, maxHeight: maxHeight }));
        }
        return null;
    };
    const _renderIconAnchorElement = () => {
        const tooltipDialogComponent = isMobileDevice || fakeMobileDevice
            ? _renderMobileTooltipDialog()
            : _renderTooltipPopover();
        const _getIconColor = () => {
            if (iconColor) {
                return iconColor;
            }
            return (surface === null || surface === void 0 ? void 0 : surface.includes('dark')) ? '#FFFFFF' : '#000000';
        };
        const _calculateIfExpanded = () => {
            if (!isMobileDevice && hasScrollableContent === undefined)
                return false;
            if (isMobileDevice && showDialog)
                return undefined;
            return showDialog;
        };
        return (jsxs("span", { className: anchorWrapperStyles.join(' '), tabIndex: isAndroidDevice ? undefined : -1, onMouseLeave: disabled ? undefined : _onMouseLeave, onMouseEnter: disabled ? undefined : _onMouseEnter, "data-testid": `${testId}-anchor-wrapper`, children: [jsxs("span", { className: anchorButtonWrapperStyles.join(' '), "data-testid": testId, id: tooltipId.current, ref: tooltipAnchorRef, children: [isOsx && hasScrollableContent && !showDialog && (jsx(ScreenReaderText, { "aria-live": "assertive", children: "collapsed" })), jsx("span", { style: {
                                outline: 'none',
                                display: 'inline-flex',
                            }, children: jsx(ButtonIcon, { "data-testid": `${testId}-anchor`, kind: "ghost", tabIndex: tabIndex, size: size === 'small' ? '20px' : '24px', surface: surface, disabled: disabled, focusBorderPosition: "inside", floating: false, showBorder: false, selectable: false, showBadgeIndicator: false, ariaLabel: ariaLabel || 'tooltip', iconOffset: {
                                    x: 0,
                                    y: 0,
                                }, renderIcon: (props) => (jsx(Info, Object.assign({}, props, { color: _getIconColor(), size: size }))), "aria-expanded": _calculateIfExpanded(), 
                                // @ts-ignore
                                onClick: disabled ? undefined : _onClick, "data-track": dataTrack, "data-track-ignore": dataTrackIgnore, "data-clickstream": dataClickStream, "data-analyticstrack": dataAnalyticsTrack }) })] }), jsx("span", { "aria-live": !isMobileDevice ? 'assertive' : undefined, "aria-relevant": "all", children: tooltipDialogComponent })] }));
    };
    const _renderCustomAnchorElement = () => {
        anchorWithRef.current = renderAnchorElement({
            'aria-expanded': showDialog,
            'aria-label': ariaLabel,
            ref: tooltipAnchorRef,
            // onKeyDown: !disabled ? _onKeyDown : undefined,
            tabIndex: 0,
            id: tooltipId.current,
            'data-track': dataTrack,
            'data-track-ignore': dataTrackIgnore,
            'data-testid': `${testId}-anchor`,
        });
        const tooltipDialogComponent = isMobileDevice
            ? _renderMobileTooltipDialog()
            : _renderTooltipPopover();
        const _renderScreenReaderText = () => {
            if (showDialog) {
                if (isFirefox)
                    return '';
                return 'expanded';
            }
            return 'collapsed';
        };
        return (jsxs(Fragment, { children: [jsxs("span", { className: customAnchorWrapperStyles.join(' '), 
                    // ref={toolTipAnchorRef} <==== unsure where this should be
                    onMouseEnter: disabled ? undefined : _onMouseEnter, onMouseLeave: disabled ? undefined : _onMouseLeave, 
                    // onMouseDown={disabled ? undefined : _onMouseDown} <=== should replace with active selector
                    // onMouseUp={disabled ? undefined : this._onMouseUp}  <=== should replace with active selector
                    onClick: disabled ? undefined : _onClick, onFocus: !disabled ? _onFocus : undefined, "data-testid": `${testId}-anchor-wrapper`, children: [isOsx && hasScrollableContent && !showDialog && (jsx(ScreenReaderText, { "aria-live": "assertive", children: "collapsed" })), !isOsx && (jsx(ScreenReaderText, { "aria-live": "assertive", children: hasScrollableContent && _renderScreenReaderText() })), jsx("div", { tabIndex: -1, children: anchorWithRef.current })] }), jsx("span", { "aria-live": !isMobileDevice ? 'assertive' : undefined, "aria-relevant": "all", children: tooltipDialogComponent })] }));
    };
    return (jsx("span", { id: tooltipWrapperId, className: tooltipWrapperStyles.join(' '), tabIndex: isAndroidDevice ? undefined : -1, "data-testid": `${testId}-wrapper`, children: renderAnchorElement
            ? _renderCustomAnchorElement()
            : _renderIconAnchorElement() }));
};

export { Tooltip, Tooltip as default };
